import { Section, CodeBlock, InfoBox } from "@/components"

export function MongoDBConfigurationPage() {
  return (
    <div className="space-y-10">
      <div className="space-y-4">
        <h1 className="text-4xl font-bold">Configuration</h1>
        <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
          Learn how to configure your MongoDB template with environment variables, 
          database connections, and authentication settings.
        </p>
      </div>

      <Section title="Environment Variables" description="Required configuration settings">
        <CodeBlock
          code={`# Application
NODE_ENV=development
PORT=3000

# MongoDB Connection
MONGODB_URI=mongodb://localhost:27017/nest-mongodb-template

# JWT Configuration
JWT_SECRET=your-super-secret-jwt-key-here
JWT_EXPIRES_IN=15m
JWT_REFRESH_SECRET=your-super-secret-refresh-key-here
JWT_REFRESH_EXPIRES_IN=7d

# Google OAuth (optional)
GOOGLE_CLIENT_ID=your-google-client-id
GOOGLE_CLIENT_SECRET=your-google-client-secret
GOOGLE_CALLBACK_URL=http://localhost:3000/api/auth/google/callback

# CORS
CORS_ORIGIN=http://localhost:5173

# Rate Limiting
RATE_LIMIT_TTL=60000
RATE_LIMIT_MAX=100`}
          language="bash"
        />
      </Section>

      <Section title="Database Configuration" description="MongoDB connection setup">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            The database configuration is handled in <code className="bg-muted px-2 py-1 rounded">src/config/database.config.ts</code>.
          </p>
          
          <CodeBlock
            code={`import { registerAs } from '@nestjs/config';
import { ConfigKey } from '@/shared/enums/config-keys.enum';

export default registerAs(ConfigKey.DATABASE, () => ({
  uri: process.env.MONGODB_URI || 'mongodb://localhost:27017/nest-mongodb-template',
}));`}
            language="typescript"
          />

          <div className="grid md:grid-cols-2 gap-6 mt-8">
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">Local Development</h3>
              <CodeBlock
                code={`MONGODB_URI=mongodb://localhost:27017/myapp`}
                language="bash"
              />
              <p className="text-sm text-muted-foreground">
                Connect to a local MongoDB instance running on your machine.
              </p>
            </div>
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">MongoDB Atlas</h3>
              <CodeBlock
                code={`MONGODB_URI=mongodb+srv://user:pass@cluster.mongodb.net/myapp`}
                language="bash"
              />
              <p className="text-sm text-muted-foreground">
                Connect to MongoDB Atlas cloud database for production use.
              </p>
            </div>
          </div>
        </div>
      </Section>

      <Section title="Application Configuration" description="App-wide settings">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            Application settings are centralized in <code className="bg-muted px-2 py-1 rounded">src/config/app.config.ts</code>.
          </p>
          
          <CodeBlock
            code={`export default registerAs(ConfigKey.APP, () => ({
  nodeEnv: process.env.NODE_ENV || 'development',
  port: parseInt(process.env.PORT || '3000', 10),
  
  jwt: {
    secret: process.env.JWT_SECRET || 'your-secret-key',
    expiresIn: process.env.JWT_EXPIRES_IN || '15m',
    refreshSecret: process.env.JWT_REFRESH_SECRET || 'your-refresh-secret',
    refreshExpiresIn: process.env.JWT_REFRESH_EXPIRES_IN || '7d',
  },

  google: {
    clientId: process.env.GOOGLE_CLIENT_ID,
    clientSecret: process.env.GOOGLE_CLIENT_SECRET,
    callbackURL: process.env.GOOGLE_CALLBACK_URL,
  },

  cors: {
    origin: process.env.CORS_ORIGIN || 'http://localhost:5173',
  },

  rateLimit: {
    ttl: parseInt(process.env.RATE_LIMIT_TTL || '60000', 10),
    limit: parseInt(process.env.RATE_LIMIT_MAX || '100', 10),
  },
}));`}
            language="typescript"
          />
        </div>
      </Section>

      <Section title="JWT Configuration" description="Token security settings">
        <div className="space-y-6">
          <InfoBox type="warning" title="Security Notice">
            Always use strong, randomly generated secrets in production. Never commit secrets to version control.
          </InfoBox>

          <div className="grid gap-6">
            <div className="space-y-3">
              <h3 className="font-semibold">JWT_SECRET</h3>
              <p className="text-sm text-muted-foreground">
                Used to sign access tokens. Should be a long, random string (at least 32 characters).
              </p>
              <CodeBlock
                code={`# Generate a secure secret
node -e "console.log(require('crypto').randomBytes(64).toString('hex'))"`}
                language="bash"
              />
            </div>

            <div className="space-y-3">
              <h3 className="font-semibold">JWT_EXPIRES_IN</h3>
              <p className="text-sm text-muted-foreground">
                Access token expiration time. Default is 15 minutes for security.
              </p>
              <CodeBlock
                code={`JWT_EXPIRES_IN=15m  # 15 minutes
JWT_EXPIRES_IN=1h   # 1 hour
JWT_EXPIRES_IN=1d   # 1 day`}
                language="bash"
              />
            </div>

            <div className="space-y-3">
              <h3 className="font-semibold">JWT_REFRESH_SECRET</h3>
              <p className="text-sm text-muted-foreground">
                Used to sign refresh tokens. Must be different from JWT_SECRET.
              </p>
            </div>

            <div className="space-y-3">
              <h3 className="font-semibold">JWT_REFRESH_EXPIRES_IN</h3>
              <p className="text-sm text-muted-foreground">
                Refresh token expiration time. Default is 7 days.
              </p>
              <CodeBlock
                code={`JWT_REFRESH_EXPIRES_IN=7d   # 7 days
JWT_REFRESH_EXPIRES_IN=30d  # 30 days`}
                language="bash"
              />
            </div>
          </div>
        </div>
      </Section>

      <Section title="OAuth Configuration" description="Google OAuth 2.0 setup">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            To enable Google OAuth, you need to create OAuth credentials in the Google Cloud Console.
          </p>

          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Setup Steps</h3>
            <ol className="space-y-3 list-decimal list-inside text-sm text-muted-foreground">
              <li>Go to the Google Cloud Console</li>
              <li>Create a new project or select an existing one</li>
              <li>Enable the Google+ API</li>
              <li>Create OAuth 2.0 credentials</li>
              <li>Add authorized redirect URIs</li>
              <li>Copy your Client ID and Client Secret</li>
            </ol>
          </div>

          <CodeBlock
            code={`GOOGLE_CLIENT_ID=123456789-abc.apps.googleusercontent.com
GOOGLE_CLIENT_SECRET=GOCSPX-abc123def456
GOOGLE_CALLBACK_URL=http://localhost:3000/api/auth/google/callback`}
            language="bash"
          />

          <InfoBox type="info" title="Optional Feature">
            OAuth is optional. The template works fine without it if you only need email/password authentication.
          </InfoBox>
        </div>
      </Section>

      <Section title="CORS Configuration" description="Cross-origin resource sharing">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            Configure CORS to allow your frontend application to communicate with the API.
          </p>

          <CodeBlock
            code={`# Single origin
CORS_ORIGIN=http://localhost:5173

# Multiple origins (comma-separated)
CORS_ORIGIN=http://localhost:5173,http://localhost:3001,https://myapp.com`}
            language="bash"
          />

          <InfoBox type="warning" title="Production">
            In production, set CORS_ORIGIN to your actual frontend domain(s) for security.
          </InfoBox>
        </div>
      </Section>
    </div>
  )
}

